import { Component } from '@theme/component';

export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  scale = 1;
  #x = 0;
  #y = 0;
  #points = new Map();
  #gesture;
  #moved = false;
  #multi = false;
  #lastTap;
  #listeners;
  #resizeObserver = new ResizeObserver(() => this.#render());

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.addEventListener('pointerdown', this.#down, options);
    this.addEventListener('pointermove', this.#move, options);
    this.addEventListener('pointerup', this.#up, options);
    this.addEventListener('pointercancel', this.#cancel, options);
    this.addEventListener('lostpointercapture', this.#cancel, options);
    this.addEventListener('wheel', this.#wheel, { ...options, passive: false });
    this.addEventListener('dragstart', this.#prevent, options);
    this.refs.image.addEventListener('load', this.#render, options);
    this.#resizeObserver.observe(this);
    this.reset();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#resizeObserver.disconnect();
    this.#points.clear();
    super.disconnectedCallback();
  }

  #prevent = (event) => event.preventDefault();

  reset() {
    this.#points.clear();
    this.#gesture = undefined;
    this.#lastTap = undefined;
    this.scale = 1;
    this.#x = 0;
    this.#y = 0;
    this.#render();
  }

  zoomBy(delta) {
    this.#zoomTo(this.scale + delta, { x: 0, y: 0 });
  }

  #local(point) {
    const bounds = this.getBoundingClientRect();
    return { x: point.x - bounds.left - bounds.width / 2, y: point.y - bounds.top - bounds.height / 2 };
  }

  #zoomTo(next, anchor) {
    next = Math.min(4, Math.max(1, next));
    const ratio = next / this.scale;
    this.#x = anchor.x - (anchor.x - this.#x) * ratio;
    this.#y = anchor.y - (anchor.y - this.#y) * ratio;
    this.scale = next;
    this.#render();
  }

  #startGesture() {
    const points = [...this.#points.values()];
    if (points.length === 2) {
      const [a, b] = points;
      this.#gesture = {
        distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
        center: this.#local({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }),
        scale: this.scale, x: this.#x, y: this.#y,
      };
    } else if (points.length === 1) {
      this.#gesture = { point: points[0], scale: this.scale, x: this.#x, y: this.#y };
    } else this.#gesture = undefined;
  }

  #down = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (this.#points.size >= 2) return;
    event.preventDefault();
    if (!this.#points.size) {
      this.#moved = false;
      this.#multi = false;
    }
    this.#points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.setPointerCapture(event.pointerId);
    if (this.#points.size === 2) {
      this.#multi = true;
      this.#lastTap = undefined;
    }
    this.#startGesture();
  };

  #move = (event) => {
    if (!this.#points.has(event.pointerId) || !this.#gesture) return;
    event.preventDefault();
    this.#points.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...this.#points.values()];
    const gesture = this.#gesture;
    if (points.length === 2 && gesture.distance) {
      const [a, b] = points;
      const center = this.#local({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
      const distance = Math.hypot(b.x - a.x, b.y - a.y);
      this.scale = Math.min(4, Math.max(1, gesture.scale * distance / gesture.distance));
      const ratio = this.scale / gesture.scale;
      this.#x = center.x - (gesture.center.x - gesture.x) * ratio;
      this.#y = center.y - (gesture.center.y - gesture.y) * ratio;
      this.#moved = true;
    } else if (points.length === 1 && gesture.point) {
      const dx = event.clientX - gesture.point.x;
      const dy = event.clientY - gesture.point.y;
      if (Math.hypot(dx, dy) > 8) this.#moved = true;
      if (this.scale > 1) {
        this.#x = gesture.x + dx;
        this.#y = gesture.y + dy;
      }
    }
    this.#render();
  };

  #up = (event) => {
    if (!this.#points.has(event.pointerId)) return;
    const gesture = this.#gesture;
    this.#points.delete(event.pointerId);
    if (!this.#points.size && !this.#multi && gesture?.point) {
      const dx = event.clientX - gesture.point.x;
      const dy = event.clientY - gesture.point.y;
      if (this.scale === 1 && this.#moved && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        this.#lastTap = undefined;
        this.dispatchEvent(new CustomEvent('mdc-gallery-step', { bubbles: true, detail: { direction: dx < 0 ? 1 : -1 } }));
      } else if (!this.#moved) {
        const now = performance.now();
        const point = { x: event.clientX, y: event.clientY };
        const doubleTap = this.#lastTap && now - this.#lastTap.time < 320 &&
          Math.hypot(point.x - this.#lastTap.x, point.y - this.#lastTap.y) < 30;
        if (event.pointerType === 'mouse' || doubleTap) {
          this.#zoomTo(this.scale > 1 ? 1 : 2, this.#local(point));
          this.#lastTap = undefined;
        } else this.#lastTap = { ...point, time: now };
      }
    }
    this.#startGesture();
    this.#render();
  };

  #cancel = (event) => {
    if (!this.#points.has(event.pointerId)) return;
    this.#points.delete(event.pointerId);
    this.#multi = true;
    this.#lastTap = undefined;
    this.#startGesture();
  };

  #wheel = (event) => {
    if (!event.deltaY) return;
    event.preventDefault();
    const delta = Math.max(-0.35, Math.min(0.35, -event.deltaY * 0.006));
    this.#zoomTo(this.scale + delta, this.#local({ x: event.clientX, y: event.clientY }));
  };

  #render = () => {
    const image = this.refs.image;
    if (!image) return;
    if (this.scale === 1) {
      // An unzoomed image is centered without measuring each hidden gallery slide.
      this.#x = 0;
      this.#y = 0;
    } else {
      const width = this.clientWidth;
      const height = this.clientHeight;
      if (width && height && image.naturalWidth && image.naturalHeight) {
        const fit = Math.min(width / image.naturalWidth, height / image.naturalHeight);
        const maxX = Math.max(0, (image.naturalWidth * fit * this.scale - width) / 2);
        const maxY = Math.max(0, (image.naturalHeight * fit * this.scale - height) / 2);
        this.#x = Math.max(-maxX, Math.min(maxX, this.#x));
        this.#y = Math.max(-maxY, Math.min(maxY, this.#y));
      }
    }
    image.style.transform = `translate3d(${this.#x}px, ${this.#y}px, 0) scale(${this.scale})`;
    this.dataset.zoomed = String(this.scale > 1);
    this.dispatchEvent(new CustomEvent('mdc-zoom-change', { bubbles: true }));
  };
}

if (!customElements.get('drag-zoom-wrapper')) customElements.define('drag-zoom-wrapper', DragZoomWrapper);
